/**
 * iMin Printer Service
 * Handles printing on iMin POS devices and fallback to browser print
 * 
 * Supports:
 * - iMin built-in printers (via JS SDK)
 * - Bluetooth printers
 * - Browser print fallback
 */

// Check if running on iMin device
export const isIminDevice = () => {
  try {
    // iMin devices expose IminPrintInstance on window
    return typeof window !== 'undefined' && 
           (window.IminPrintInstance !== undefined || 
            navigator.userAgent.toLowerCase().includes('imin'));
  } catch {
    return false;
  }
};

// Get iMin printer instance
const getIminPrinter = () => {
  if (typeof window !== 'undefined' && window.IminPrintInstance) {
    return window.IminPrintInstance;
  }
  return null;
};

// Initialize iMin printer
export const initPrinter = async (connectionType = 'auto') => {
  const printer = getIminPrinter();
  if (!printer) {
    console.log('iMin printer not available, will use browser print');
    return { success: false, method: 'browser' };
  }

  try {
    // Connection types: SPI, USB, Bluetooth
    let type = printer.PrintConnectType?.SPI;
    if (connectionType === 'usb') type = printer.PrintConnectType?.USB;
    if (connectionType === 'bluetooth') type = printer.PrintConnectType?.Bluetooth;
    
    printer.initPrinter(type);
    console.log('iMin printer initialized with connection:', connectionType);
    return { success: true, method: 'imin' };
  } catch (error) {
    console.error('Failed to initialize iMin printer:', error);
    return { success: false, method: 'browser', error };
  }
};

// Get printer status
export const getPrinterStatus = async () => {
  const printer = getIminPrinter();
  if (!printer) return { status: -1, message: 'Not connected' };

  return new Promise((resolve) => {
    try {
      printer.getPrinterStatus(printer.PrintConnectType?.SPI, (status) => {
        const statusMap = {
          '-1': 'Not connected',
          '1': 'Normal',
          '3': 'Print head open',
          '7': 'No paper',
          '8': 'Paper running out',
          '99': 'Other error'
        };
        resolve({
          status: status.value,
          message: statusMap[status.value] || 'Unknown'
        });
      });
    } catch (error) {
      resolve({ status: -1, message: 'Error checking status' });
    }
  });
};

// Print receipt using iMin SDK
export const printReceiptImin = async (receiptData, paperSize = '80mm') => {
  const printer = getIminPrinter();
  if (!printer) {
    throw new Error('iMin printer not available');
  }

  try {
    // Set paper format
    printer.setPageFormat(paperSize === '58mm' ? 1 : 0);
    
    // Initialize printer
    printer.initPrinter(printer.PrintConnectType?.SPI);

    // Print logo if available
    if (receiptData.logoUrl) {
      printer.setAlignment(1); // Center
      printer.printSingleBitmap(receiptData.logoUrl);
      printer.printAndLineFeed();
    }

    // Header - Business name
    printer.setAlignment(1); // Center
    printer.setTextSize(32);
    printer.setTextStyle(1); // Bold
    printer.printText(receiptData.businessName + '\n');
    
    printer.setTextSize(24);
    printer.setTextStyle(0); // Normal
    if (receiptData.businessAddress) {
      printer.printText(receiptData.businessAddress + '\n');
    }
    if (receiptData.businessPhone) {
      printer.printText(`Tel: ${receiptData.businessPhone}\n`);
    }
    if (receiptData.kraPin) {
      printer.printText(`PIN: ${receiptData.kraPin}\n`);
    }

    // Custom header items
    if (receiptData.headerItems?.length > 0) {
      printer.setTextSize(22);
      for (const item of receiptData.headerItems) {
        if (item.type === 'text') {
          printer.printText(item.text + '\n');
        } else {
          printer.printText(`${item.key}: ${item.value}\n`);
        }
      }
    }

    // Divider
    printer.printText('--------------------------------\n');

    if (receiptData.documentTitle) {
      printer.setTextStyle(1);
      printer.setTextSize(28);
      printer.printText(`${receiptData.documentTitle}\n`);
      printer.setTextStyle(0);
      printer.setTextSize(24);
    }

    // Invoice details
    printer.setAlignment(0); // Left
    printer.setTextSize(24);
    const invoiceLabel = receiptData.documentType === 'credit_note' ? 'Credit Note' : 'Invoice';
    printer.printText(`${invoiceLabel}: ${receiptData.invoiceNumber}\n`);
    if (receiptData.originalInvoiceNumber) {
      printer.printText(`Original Invoice: ${receiptData.originalInvoiceNumber}\n`);
    }
    if (receiptData.creditNoteReason) {
      printer.printText(`Reason: ${receiptData.creditNoteReason}\n`);
    }
    printer.printText(`Date: ${receiptData.date} Time: ${receiptData.time}\n`);
    
    if (receiptData.serverName) {
      printer.printText(`Served By: ${receiptData.serverName}\n`);
    }
    if (receiptData.tableName) {
      printer.printText(`Table: ${receiptData.tableName}\n`);
    }
    if (receiptData.customerName) {
      printer.printText(`Customer: ${receiptData.customerName}\n`);
    }

    printer.printText('--------------------------------\n');

    // Items header
    const width = paperSize === '58mm' ? 384 : 576;
    printer.printColumnsText(['Qty', 'Item', 'Amount'], [1, 4, 2], [0, 0, 2], [22, 22, 22], width);
    printer.printText('--------------------------------\n');

    // Items
    if (receiptData.groupByCategory && receiptData.groupedItems) {
      for (const [category, items] of Object.entries(receiptData.groupedItems)) {
        printer.setTextStyle(1); // Bold
        printer.setAlignment(1);
        printer.printText(`-- ${category} --\n`);
        printer.setTextStyle(0);
        printer.setAlignment(0);
        
        for (const item of items) {
          const amount = (item.price * item.quantity).toFixed(2);
          printer.printColumnsText(
            [item.quantity.toString(), item.name, amount],
            [1, 4, 2], [0, 0, 2], [22, 22, 22], width
          );
        }
      }
    } else {
      for (const item of receiptData.items || []) {
        const amount = (item.price * item.quantity).toFixed(2);
        printer.printColumnsText(
          [item.quantity.toString(), item.name, amount],
          [1, 4, 2], [0, 0, 2], [22, 22, 22], width
        );
      }
    }

    printer.printText('--------------------------------\n');

    // Totals
    printer.setAlignment(0);
    printer.printColumnsText(['Subtotal', '', receiptData.subtotal?.toFixed(2) || '0.00'], [2, 2, 2], [0, 0, 2], [24, 24, 24], width);
    
    // Tax breakdown or single tax
    if (receiptData.showDetailedTaxes && receiptData.taxBreakdown?.length > 0) {
      for (const tax of receiptData.taxBreakdown) {
        printer.printColumnsText([`${tax.name} (${tax.rate}%)`, '', tax.amount?.toFixed(2)], [3, 1, 2], [0, 0, 2], [22, 22, 22], width);
      }
    } else {
      printer.printColumnsText(['Tax', '', receiptData.tax?.toFixed(2) || '0.00'], [2, 2, 2], [24, 24, 24], width);
    }

    if (receiptData.serviceCharge > 0) {
      printer.printColumnsText(['Service Charge', '', receiptData.serviceCharge.toFixed(2)], [3, 1, 2], [0, 0, 2], [22, 22, 22], width);
    }

    if (receiptData.discount > 0) {
      printer.printColumnsText(['Discount', '', `-${receiptData.discount.toFixed(2)}`], [2, 2, 2], [0, 0, 2], [24, 24, 24], width);
    }

    printer.setTextStyle(1); // Bold
    printer.setTextSize(28);
    printer.printColumnsText([receiptData.labels?.total || 'TOTAL', '', receiptData.total?.toFixed(2) || '0.00'], [2, 2, 2], [0, 0, 2], [28, 28, 28], width);
    printer.setTextStyle(0);
    printer.setTextSize(24);

    // Payment method
    if (receiptData.paymentMethod) {
      printer.printText(`Payment: ${receiptData.paymentMethod.toUpperCase()}\n`);
    }

    // Loyalty points
    if (receiptData.pointsEarned > 0) {
      printer.printText(`Points Earned: +${receiptData.pointsEarned}\n`);
    }

    printer.printText('--------------------------------\n');

    // Footer items
    printer.setAlignment(1);
    if (receiptData.footerItems?.length > 0) {
      for (const item of receiptData.footerItems) {
        if (item.type === 'text') {
          printer.printText(item.text + '\n');
        } else {
          printer.printText(`${item.key}: ${item.value}\n`);
        }
      }
    }

    // QR Code for feedback
    if (receiptData.showQrCode && receiptData.qrCodeUrl) {
      printer.printAndLineFeed();
      printer.setQrCodeSize(5);
      printer.printQrCode(receiptData.qrCodeUrl, 1);
      printer.setTextSize(20);
      printer.printText('Scan for feedback\n');
    }

    // eTIMS QR Code
    if (receiptData.etims && receiptData.etims.submitted) {
      const isCreditNote = receiptData.etims.document_type === 'credit_note' || receiptData.documentType === 'credit_note';
      printer.printAndLineFeed();
      printer.setAlignment(1);
      printer.printText('--------------------------------\n');
      printer.setTextStyle(1);
      printer.setTextSize(22);
      printer.printText(isCreditNote ? 'KRA eTIMS CREDIT NOTE\n' : 'KRA eTIMS VERIFIED\n');
      printer.setTextStyle(0);
      printer.setTextSize(20);
      printer.printText(`CU: ${receiptData.etims.sdc_id || ''}\n`);
      printer.printText(`${isCreditNote ? 'Credit Note' : 'Invoice'} #: ${receiptData.etims.invc_no || ''}\n`);
      printer.printText(`Sign: ${receiptData.etims.rcpt_sign || ''}\n`);
      if (receiptData.kraPin) {
        printer.printText(`TIN: ${receiptData.kraPin}\n`);
      }
      printer.printAndLineFeed();
      printer.setQrCodeSize(6);
      printer.printQrCode(receiptData.etims.qr_data, 1);
      printer.setTextSize(18);
      printer.printText('Scan to verify receipt\n');
      printer.printText('--------------------------------\n');
    }

    // Reprint indicator
    if (receiptData.isReprint) {
      printer.printAndLineFeed();
      printer.setTextStyle(1);
      printer.printText('*** REPRINT ***\n');
    }

    // Minimal paper feed before cut (reduced from 100 to 30)
    printer.printAndFeedPaper(30);
    printer.partialCut();

    return { success: true, method: 'imin' };
  } catch (error) {
    console.error('iMin print error:', error);
    throw error;
  }
};

// Print kitchen ticket using iMin SDK
export const printKitchenTicketImin = async (ticketData, paperSize = '80mm') => {
  const printer = getIminPrinter();
  if (!printer) {
    throw new Error('iMin printer not available');
  }

  try {
    const is58mm = paperSize === '58mm';
    printer.setPageFormat(is58mm ? 1 : 0);
    printer.initPrinter(printer.PrintConnectType?.SPI);

    // Header - destination name
    printer.setAlignment(1);
    printer.setTextSize(is58mm ? 28 : 36);
    printer.setTextStyle(1);
    printer.printText((ticketData.destination || 'KITCHEN').toUpperCase() + '\n');
    
    // Outlet name if provided
    if (ticketData.outletName) {
      printer.setTextSize(is58mm ? 22 : 26);
      printer.printText(ticketData.outletName + '\n');
    }
    
    printer.printText(is58mm ? '------------------------\n' : '--------------------------------\n');

    // Table number - prominent
    printer.setTextSize(is58mm ? 26 : 32);
    printer.printText(`Table: ${ticketData.tableNumber || 'N/A'}\n`);
    
    // Order info
    printer.setTextSize(is58mm ? 22 : 28);
    printer.setTextStyle(0);
    printer.printText(`Order: ${ticketData.orderNumber || ''}\n`);
    printer.printText(`${new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}\n`);
    if (ticketData.orderType) {
      printer.printText(`${ticketData.orderType}\n`);
    }
    // Waiter/User name
    if (ticketData.waiterName) {
      printer.printText(`Waiter: ${ticketData.waiterName}\n`);
    }

    printer.printText(is58mm ? '------------------------\n' : '--------------------------------\n');

    // Items
    printer.setAlignment(0);
    printer.setTextSize(is58mm ? 26 : 32);
    printer.setTextStyle(1);
    
    for (const item of ticketData.items || []) {
      printer.printText(`${item.quantity}x ${item.name}\n`);
      if (item.notes) {
        printer.setTextSize(is58mm ? 20 : 24);
        printer.setTextStyle(2); // Italic
        printer.printText(`  > ${item.notes}\n`);
        printer.setTextSize(is58mm ? 26 : 32);
        printer.setTextStyle(1);
      }
      if (item.modifiers?.length) {
        printer.setTextSize(is58mm ? 20 : 24);
        printer.setTextStyle(0);
        for (const mod of item.modifiers) {
          printer.printText(`  + ${mod}\n`);
        }
        printer.setTextSize(is58mm ? 26 : 32);
        printer.setTextStyle(1);
      }
    }

    printer.printText(is58mm ? '------------------------\n' : '--------------------------------\n');

    // Reprint indicator
    if (ticketData.isReprint) {
      printer.setAlignment(1);
      printer.setTextStyle(1);
      printer.printText('*** REPRINT ***\n');
    }

    // Minimal paper feed before cut
    printer.printAndFeedPaper(30);
    printer.partialCut();

    return { success: true, method: 'imin' };
  } catch (error) {
    console.error('iMin kitchen print error:', error);
    throw error;
  }
};

// Browser print fallback
export const printBrowser = (htmlContent) => {
  const printWindow = window.open('', '_blank', 'width=400,height=600');
  if (!printWindow) {
    throw new Error('Could not open print window. Please allow popups.');
  }

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => printWindow.close(), 1000);
  };

  return { success: true, method: 'browser' };
};

// Open cash drawer
export const openCashDrawer = () => {
  const printer = getIminPrinter();
  if (printer && typeof printer.openCashBox === 'function') {
    try {
      printer.openCashBox();
      return { success: true };
    } catch (error) {
      console.error('Failed to open cash drawer:', error);
      return { success: false, error };
    }
  }
  return { success: false, error: 'Cash drawer not supported' };
};

// Main print function - auto-selects method
export const printReceipt = async (receiptData, config = {}) => {
  const { 
    printerConnection = 'auto', 
    paperSize = '80mm',
    fallbackToBrowser = true 
  } = config;

  // Try iMin first if auto or imin specified
  if ((printerConnection === 'auto' || printerConnection === 'builtin') && isIminDevice()) {
    try {
      return await printReceiptImin(receiptData, paperSize);
    } catch (error) {
      console.warn('iMin print failed, falling back to browser:', error);
      if (!fallbackToBrowser) throw error;
    }
  }

  // Browser fallback
  if (fallbackToBrowser || printerConnection === 'browser') {
    const htmlContent = generateReceiptHTML(receiptData, paperSize);
    return printBrowser(htmlContent);
  }

  throw new Error('No suitable print method available');
};

// Generate HTML for browser printing
const generateReceiptHTML = (data, paperSize) => {
  const width = paperSize === '58mm' ? '48mm' : '72mm';
  const isCreditNote = data.documentType === 'credit_note' || data.etims?.document_type === 'credit_note';
  
  // Generate eTIMS section if available
  const etimsSection = data.etims && data.etims.submitted ? `
    <div class="divider"></div>
    <div class="center bold" style="font-size:14px;">${isCreditNote ? 'KRA eTIMS CREDIT NOTE' : 'KRA eTIMS VERIFIED'}</div>
    <div class="center" style="font-size:10px;">CU: ${data.etims.sdc_id || ''}</div>
    <div class="center" style="font-size:10px;">${isCreditNote ? 'Credit Note' : 'Invoice'} #: ${data.etims.invc_no || ''}</div>
    <div class="center" style="font-size:10px;">Sign: ${data.etims.rcpt_sign || ''}</div>
    ${data.kraPin ? `<div class="center" style="font-size:10px;">TIN: ${data.kraPin}</div>` : ''}
    ${data.etims.qr_base64 ? `
      <div class="center" style="margin:8px 0;">
        <img src="data:image/png;base64,${data.etims.qr_base64}" style="width:120px;height:120px;" alt="eTIMS QR"/>
      </div>
    ` : ''}
    <div class="center" style="font-size:9px;">Scan to verify at KRA</div>
  ` : '';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${data.documentTitle || 'Receipt'}</title>
      <style>
        @media print {
          @page { margin: 0; size: ${paperSize} auto; }
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          width: ${width};
          margin: 0 auto;
          padding: 5mm;
        }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .divider { border-top: 1px dashed #000; margin: 5px 0; }
        .row { display: flex; justify-content: space-between; }
        .large { font-size: 16px; }
      </style>
    </head>
    <body>
      ${data.logoUrl ? `<div class="center"><img src="${data.logoUrl}" style="max-width:100px;max-height:40px"/></div>` : ''}
      <div class="center bold large">${data.businessName || 'Receipt'}</div>
      ${data.businessAddress ? `<div class="center">${data.businessAddress}</div>` : ''}
      ${data.businessPhone ? `<div class="center">Tel: ${data.businessPhone}</div>` : ''}
      ${data.kraPin ? `<div class="center">PIN: ${data.kraPin}</div>` : ''}
      <div class="divider"></div>
      ${data.documentTitle ? `<div class="center bold large">${data.documentTitle}</div>` : ''}
      <div>${isCreditNote ? 'Credit Note' : 'Invoice'}: ${data.invoiceNumber || ''}</div>
      ${data.originalInvoiceNumber ? `<div>Original Invoice: ${data.originalInvoiceNumber}</div>` : ''}
      ${data.creditNoteReason ? `<div>Reason: ${data.creditNoteReason}</div>` : ''}
      <div>Date: ${data.date || ''} ${data.time || ''}</div>
      ${data.serverName ? `<div>Served By: ${data.serverName}</div>` : ''}
      ${data.tableName ? `<div>Table: ${data.tableName}</div>` : ''}
      <div class="divider"></div>
      ${(data.items || []).map(item => `
        <div class="row">
          <span>${item.quantity}x ${item.name}</span>
          <span>${(item.price * item.quantity).toFixed(2)}</span>
        </div>
      `).join('')}
      <div class="divider"></div>
      <div class="row"><span>Subtotal</span><span>${data.subtotal?.toFixed(2) || '0.00'}</span></div>
      <div class="row"><span>Tax</span><span>${data.tax?.toFixed(2) || '0.00'}</span></div>
      <div class="row bold large"><span>${data.labels?.total || 'TOTAL'}</span><span>${data.total?.toFixed(2) || '0.00'}</span></div>
      ${etimsSection}
      <div class="divider"></div>
      ${data.footerItems?.map(item => `<div class="center">${item.type === 'text' ? item.text : `${item.key}: ${item.value}`}</div>`).join('') || ''}
      ${data.isReprint ? '<div class="center bold">*** REPRINT ***</div>' : ''}
    </body>
    </html>
  `;
};

export default {
  isIminDevice,
  initPrinter,
  getPrinterStatus,
  printReceipt,
  printReceiptImin,
  printKitchenTicketImin,
  printBrowser,
  openCashDrawer
};
